// Auth service. Owns signIn / signUp / signOut / restoreSession and a
// subscribe API. The real implementation lives in `pocketbase.ts`; this
// module re-exports the AuthService interface, the AuthUser shape, and an
// AuthProvider that wires it into React.
//
// `loading` starts true and only settles after restoreSession resolves —
// rendering the guard before that completes flashes the login screen at
// every already-signed-in user on every page load.

import PocketBase from "pocketbase";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { pbUrl } from "./pocketbase";
import {
  AuthError,
  classifyAuthError,
  classifyResetError,
} from "./authErrors";
import {
  deleteActiveDb,
  getDb,
  discardEmptyStarts,
  openDbFor,
  setOwner,
} from "./db";
import { isWorkoutActive } from "../hooks/workoutActive";
import { requestPersistence } from "./storage";
import { sync as syncEngine } from "./sync";

export type Role = "athlete" | "coach";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
}

export interface AuthService {
  signIn(email: string, password: string): Promise<AuthUser>;
  signUp(input: {
    email: string;
    password: string;
    displayName: string;
    role: Role;
  }): Promise<AuthUser>;
  signOut(opts?: { force?: boolean }): Promise<void>;
  deleteAccount(): Promise<void>;
  /** R-11: trigger a password-reset email. The PocketBase SDK always
   *  resolves with `true` (or throws on network failure), so the caller
   *  cannot use the return value to decide whether the account exists.
   *  The neutral confirmation screen is what closes the enumeration
   *  hole (F-6 follow-up). Throws `AuthError("offline")` when
   *  navigator.onLine === false, otherwise any thrown SDK error
   *  propagates. */
  requestPasswordReset(email: string): Promise<void>;
  /** R-11: consume a reset token from the email link and set a new
   *  password. Throws an `AuthError` whose `code` is one of
   *  `"invalid-token"`, `"offline"`, or `"unknown"` on failure. */
  confirmPasswordReset(
    token: string,
    password: string,
    passwordConfirm: string,
  ): Promise<void>;
  getRawClient?(): PocketBase | null;
  /** May be sync or async; the AuthProvider handles both. */
  restoreSession(): AuthUser | null | Promise<AuthUser | null>;
  subscribe(cb: (user: AuthUser | null) => void): () => void;
}

// --- The real service implementation --------------------------------------

/** The real PocketBase-backed service. The SDK is loaded lazily so tests
 *  that pass a fake service never trigger a network call. */
class PocketBaseAuthService implements AuthService {
  #client: PocketBase | null = null;
  #listeners = new Set<(u: AuthUser | null) => void>();
  #pbUrl: string;

  constructor(pbUrl: string) {
    this.#pbUrl = pbUrl;
  }

  #getClient(): PocketBase {
    if (!this.#client) {
      this.#client = new PocketBase(this.#pbUrl || "/");
    }
    return this.#client;
  }

  #emit() {
    const u = this.currentUser();
    for (const l of this.#listeners) l(u);
  }

  currentUser(): AuthUser | null {
    const c = this.#client;
    if (!c) return null;
    const record = c.authStore.record;
    if (!record || !c.authStore.isValid) return null;
    return {
      id: record.id,
      email: (record as { email?: string }).email ?? "",
      // F-2: drop the displayName fallback. The schema field is `name`.
      displayName: (record as { name?: string }).name ?? "",
      role: ((record as { role?: string }).role ?? "athlete") as Role,
    };
  }

  async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const c = this.#getClient();
      await c.collection("users").authWithPassword(email, password);
    } catch (err) {
      throw new AuthError(classifyAuthError(err));
    }
    this.#emit();
    const u = this.currentUser();
    if (!u) throw new AuthError("unknown");
    return u;
  }

  async signUp(input: {
    email: string;
    password: string;
    displayName: string;
    role: Role;
  }): Promise<AuthUser> {
    try {
      const c = this.#getClient();
      await c.collection("users").create({
        email: input.email,
        password: input.password,
        passwordConfirm: input.password,
        // F-2: the live schema column is `name`; sending `displayName`
        // was silently dropped and every account created so far has
        // name: "". The orchestrator restored required + min: 2 in T-028.
        name: input.displayName,
        role: input.role,
        emailVisibility: false,
      });
      await c.collection("users").authWithPassword(input.email, input.password);
    } catch (err) {
      throw new AuthError(classifyAuthError(err));
    }
    this.#emit();
    const u = this.currentUser();
    if (!u) throw new AuthError("unknown");
    return u;
  }

  async signOut(): Promise<void> {
    try {
      this.#client?.authStore.clear();
    } catch {
      /* ignore */
    }
    this.#emit();
  }

  async deleteAccount(): Promise<void> {
    const c = this.#getClient();
    const record = c.authStore.record;
    if (!record) throw new Error("deleteAccount: not signed in");
    await c.collection("users").delete(record.id);
  }

  async requestPasswordReset(email: string): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new AuthError("offline");
    }
    try {
      await this.#getClient().collection("users").requestPasswordReset(email);
    } catch (err) {
      // F-6 follow-up: we never reveal whether the email exists. A
      // server-side validation error (malformed email format) would
      // still throw, so we narrow: PocketBase's
      // `requestPasswordReset` always 204s whether or not the user
      // exists; the only realistic non-2xx is a 400 for a syntactically
      // invalid email and a network failure. Both should bubble as
      // `unknown` so the UI can fall back to the neutral "if an account
      // exists we sent a mail" confirmation rather than a per-account
      // disclosure. Offline is the only distinct surface.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new AuthError("offline");
      }
      throw new AuthError(classifyAuthError(err));
    }
  }

  async confirmPasswordReset(
    token: string,
    password: string,
    passwordConfirm: string,
  ): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new AuthError("offline");
    }
    try {
      await this.#getClient()
        .collection("users")
        .confirmPasswordReset(token, password, passwordConfirm);
    } catch (err) {
      throw new AuthError(classifyResetError(err));
    }
  }

  async restoreSession(): Promise<AuthUser | null> {
    try {
      const c = this.#getClient();
      // F-4: return the stored user immediately (offline-safe, fast
      // loading gate). Then in the background, ask the server to
      // confirm the token is still valid. On 401/403/404, the token
      // has been revoked, deleted, or role/name changed: clear the
      // store and emit. Never block the gate on the network.
      if (c.authStore.isValid) {
        const user = this.currentUser();
        this.#backgroundRefresh();
        return user;
      }
      return null;
    } catch {
      return null;
    }
  }

  #backgroundRefresh(): void {
    void (async () => {
      try {
        const c = this.#getClient();
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        // PocketBase's `authRefresh` re-validates the token and refreshes
        // the auth record. We do not have a direct SDK call here, so
        // we hit the `users/auth-refresh` endpoint via the SDK's
        // `authStore` record. If the token is invalid the SDK throws
        // a 401; we catch and clear.
        await c.collection("users").authRefresh();
        this.#emit();
      } catch (err) {
        const status = (err as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) {
          try {
            this.#client?.authStore.clear();
          } catch {
            /* ignore */
          }
          this.#emit();
        }
      }
    })();
  }

  getRawClient(): PocketBase | null {
    return this.#client;
  }

  subscribe(cb: (u: AuthUser | null) => void): () => void {
    this.#listeners.add(cb);
    return () => {
      this.#listeners.delete(cb);
    };
  }
}

let _service: AuthService | null = null;

/** Get (or lazily construct) the singleton real auth service. */
export function getAuthService(): AuthService {
  if (_service) return _service;
  _service = new PocketBaseAuthService(pbUrl);
  return _service;
}

/** For tests: replace the singleton service. */
export function setAuthService(svc: AuthService | null): void {
  _service = svc;
}

// --- React provider --------------------------------------------------------

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<AuthUser>;
  signUp(input: {
    email: string;
    password: string;
    displayName: string;
    role: Role;
  }): Promise<AuthUser>;
  signOut(opts?: { force?: boolean }): Promise<void>;
  deleteAccount(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  confirmPasswordReset(
    token: string,
    password: string,
    passwordConfirm: string,
  ): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * F-7: the signOut protocol awaits a best-effort sync, bounded to
 * this many milliseconds. Production default is 5 s (ADR-0007 D6:
 * the user should not wait more than that on sign-out). Tests can
 * lower this via `setSignOutSyncTimeoutMs(0)` to make signOut
 * synchronous w.r.t. the test runner.
 */
let signOutSyncTimeoutMs = 5000;
export function setSignOutSyncTimeoutMs(ms: number): void {
  signOutSyncTimeoutMs = Math.max(0, ms);
}

export function AuthProvider({
  children,
  service,
}: {
  children: ReactNode;
  /** Test seam: pass a fake service. In production we use the singleton. */
  service?: AuthService;
}): React.ReactElement {
  const svc = service ?? getAuthService();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const restoredRef = useRef(false);

  useEffect(() => {
    // Settle the loading gate. The service may return a promise (the
    // real PocketBase path) or a plain value (a synchronous test stub).
    // The AuthProvider is the canonical writer of `user` and `loading`.
    let settled = false;
    const finish = (restored: AuthUser | null) => {
      if (settled) return;
      settled = true;
      setUser(restored);
      restoredRef.current = true;
      setLoading(false);
    };
    try {
      const result = svc.restoreSession();
      if (result && typeof (result as Promise<unknown>).then === "function") {
        (result as Promise<AuthUser | null>)
          .then(finish)
          .catch(() => finish(null));
      } else {
        finish(result as AuthUser | null);
      }
    } catch {
      finish(null);
    }
  }, [svc]);

  useEffect(() => {
    const unsub = svc.subscribe((u) => {
      setUser(u);
    });
    return unsub;
  }, [svc]);

  // F-1: open the per-user database and set the owner whenever a user
  // becomes non-null. On sign-out (user = null), run the F-7 sign-out
  // protocol: best-effort sync, dispose the sync engine, clear the
  // auth store, delete that user's database, set owner to null. The
  // `useEffect` re-runs on every `user` change so sign-in and sign-out
  // both go through this one place. We `await` the effect on
  // sign-out by exposing a `signOut` callback that also triggers the
  // user=null transition — see the explicit `signOut` below.
  useEffect(() => {
    if (user) {
      try {
        openDbFor(user.id);
      } catch {
        /* ignore — if the existing db has the same name it's fine */
      }
      setOwner(user.id);
      // ADR-0013 D3: startup sweep tombstones every workout_logs row
      // of this owner that has no log_entries and is older than 60 s.
      // Closes the gap that an unmount-time cleanup cannot: a browser
      // crash or OS kill that never ran React's cleanup. Idempotent.
      // T-096 §5: this effect re-fires on the background `authRefresh`
      // emit; never sweep while a workout is on screen — its zero-set
      // row is the one being trained, not an abandoned start.
      if (!isWorkoutActive()) {
        void discardEmptyStarts(user.id).catch(() => undefined);
      }
      // ADR-0007 D6: request storage persistence once after first login.
      void requestPersistence();
    } else {
      setOwner(null);
    }
  }, [user]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      return svc.signIn(email, password);
    },
    [svc],
  );
  const signUp = useCallback(
    async (input: { email: string; password: string; displayName: string; role: Role }) => {
      return svc.signUp(input);
    },
    [svc],
  );
  const deleteAccount = useCallback(async () => {
    return svc.deleteAccount();
  }, [svc]);
  const requestPasswordReset = useCallback(
    async (email: string) => svc.requestPasswordReset(email),
    [svc],
  );
  const confirmPasswordReset = useCallback(
    async (token: string, password: string, passwordConfirm: string) =>
      svc.confirmPasswordReset(token, password, passwordConfirm),
    [svc],
  );
  const signOut = useCallback(async (opts?: { force?: boolean }) => {
    // F-1 + F-7 + ADR-0007 D6 sign-out protocol:
    //   1. If the outbox is non-empty, require explicit force or throw
    //      so the UI can show a confirmation.
    //   2. Run one best-effort sync (5 s timeout).
    //   3. Dispose the sync engine so no late retries run against the
    //      deleted db.
    //   4. Clear the auth store.
    //   5. Delete that user's database and set owner to null.
    let queueLength = 0;
    try {
      const db = getDb();
      // T-097 §4 (ADR-0003 rev 2 audit): the queue holds entries of
      // three statuses today (`pending`, `failed`, `held`). A held
      // entry is NOT unsynced user data — it is a row we wrote
      // locally on purpose and deliberately have not pushed yet
      // (e.g. a `workout_logs` create whose owner has not yet logged
      // a set). Counting it as unsynced would (a) demand the
      // confirmation sentence the user has no reason to see and
      // (b) trigger a best-effort sync that flushes nothing for it.
      // Count `pending|failed` only.
      const queue = await db.syncQueue
        .toArray()
        .catch(() => [] as Array<{ status: string }>);
      queueLength = queue.filter(
        (e) => e.status === "pending" || e.status === "failed",
      ).length;
      if (queueLength > 0 && !opts?.force) {
        throw new Error("signOut.unsynced");
      }
      if (queueLength > 0) {
        const timeout = new Promise<void>((resolve) =>
          setTimeout(resolve, signOutSyncTimeoutMs),
        );
        await Promise.race([syncEngine.syncNow(), timeout]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === "signOut.unsynced") {
        throw err;
      }
      /* best-effort for other sync errors */
    }
    try {
      // F-7: dispose releases the online/visibilitychange listeners
      // and detaches the local-write subscription, and clears the
      // in-flight promise. F-7 requires this BEFORE db.delete so no
      // late retries fire.
      syncEngine.dispose();
    } catch {
      /* sync may not be initialized in some test paths */
    }
    await svc.signOut();
    await deleteActiveDb();
    setOwner(null);
  }, [svc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signOut,
      deleteAccount,
      requestPasswordReset,
      confirmPasswordReset,
    }),
    [user, loading, signIn, signUp, signOut, deleteAccount, requestPasswordReset, confirmPasswordReset],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// Re-export the error utilities so the LoginPage / SignupPage can use them.
export { AuthError, classifyAuthError };
